
import {
    applyMiddleware,
    createStore,
    StoreEnhancer,
    compose
} from 'redux';
import thunk from 'redux-thunk';
import { batchedSubscribe } from 'redux-batched-subscribe';
import { nextStoreUpdateEnhancer } from './enhancers/NextStoreUpdateEnhancer';
import { runOnceMiddleware } from './middleware/RunOnceMiddleware';
import { loggingMiddleware } from './middleware/LoggingMiddleware';
import { IAction, IDispatch } from './actions/createActionCreator';
import { IFaceted, pack, Facet } from '../models/Facet';
import { IItemCacheState, itemCacheReducer } from './reducers/itemCacheReducer';
import { IItemCacheDispatch, IItemCacheContext } from './actions/ItemCacheActions';
import { resolveItems, supportFacets } from '../operations/getItems';
import { configurationItemKey, configurationItemKeyFacet } from '../facets/ConfigurationFacets';
import { registerPermanentItemDemands } from '../operations/pruneItems';
import { withGraft, graft as defaultGraft } from '../utilities/Graft';

/**
 * @public
 */
export interface IReducer<IState> {
    (state: IState, action: IAction): IState;
}

/**
 * @public
 */
export interface IStore<IState> {
    readonly dispatch: IDispatch<IState>;
    getState(): IState;
    subscribe(subscriber: () => void): () => void;
    replaceReducer(reducer: IReducer<IState>): void;
}

/**
 * @public
 */
export type IItemCacheConfiguration = IFaceted;

/**
 * @public
 */
export interface IItemCacheStore extends IStore<IItemCacheState> {
    readonly dispatch: IItemCacheDispatch;
    dispose(): void;
}

/**
 * @public
 */
export interface IStoreNotifier {
    (notify: () => void, options?: { urgent?: boolean; }): void;
}

/**
 * @public
 */
export interface ICreateItemCacheStoreOptions {
    configuration?: IItemCacheConfiguration;
    createStore?: typeof createStore;
}

/**
 * @internal
 */
export const DEFAULT_CONFIGURATION: IItemCacheConfiguration = pack({}, {});

/**
 * @internal
 */
export const notifierFacet: Facet<IStoreNotifier> = new Facet('storeNotifier');

/**
 * @public
 */
export function createItemCacheStore(options: ICreateItemCacheStoreOptions): IItemCacheStore & {
    dispose: () => void;
} {
    const {
        configuration = DEFAULT_CONFIGURATION,
        createStore: storeCreator = createStore
    } = options;

    const cacheContext: IItemCacheContext = {
        // This is for legacy compatibility and should be removed in the next major version.
        configuration: configuration
    };

    const {
        notifier,
        forceNotify
    } = wrapNotifier()(notifierFacet.unpack(configuration) || createDefaultStoreNotifier());

    const finalItemCacheReducer = (state: IItemCacheState | undefined, action: IAction): IItemCacheState => {
        return withGraft({ freeze: true })((graft: typeof defaultGraft) => {
            return itemCacheReducer(state, action, { graft });
        });
    };

    const store = storeCreator(
        finalItemCacheReducer,
        compose(
            applyMiddleware(
                loggingMiddleware,
                runOnceMiddleware,
                thunk.withExtraArgument(cacheContext)
            ),
            nextStoreUpdateEnhancer(forceNotify),
            batchedSubscribe(notifier)
        ) as StoreEnhancer<IItemCacheStore, IItemCacheState>);

    // Prevent requests from accumulating toward configuration.
    store.dispatch(supportFacets({
        keyFacet: configurationItemKeyFacet,
        facets: []
    }));

    store.dispatch(resolveItems({
        items: {
            [configurationItemKey]: configuration
        }
    }));

    store.dispatch(registerPermanentItemDemands({
        items: {
            [configurationItemKey]: {}
        }
    }));

    return {
        ...store,
        dispose: () => {
            // Nothing right now. In the future, this will dispose any middleware
            // that requires lifetime management.
        }
    };
}

/**
 * @internal
 */
export function createDefaultStoreNotifier(): typeof notifierFacet.type {
    let timeoutId: number | null = null;

    function defaultNotifier(notify: () => void, options: { urgent?: boolean; } = {}): void {
        const { urgent } = options;

        if (urgent) {
            if (timeoutId !== null) {
                window.clearTimeout(timeoutId);
                timeoutId = null;
            }

            notify();
        } else {
            if (timeoutId === null) {
                timeoutId = window.setTimeout(() => {
                    timeoutId = null;

                    notify();
                }, 0);
            }
        }
    }

    return defaultNotifier;
}

function wrapNotifier(): (notifier: typeof notifierFacet.type) => {
    notifier: typeof notifierFacet.type;
    forceNotify: () => void;
} {
    return (originalNotifier: typeof notifierFacet.type) => {
        let lastNotify: (() => void) | undefined;

        return {
            notifier: (notify: () => void) => {
                lastNotify = notify;

                originalNotifier(notify);
            },
            forceNotify: () => {
                const notify = lastNotify;

                if (notify) {
                    originalNotifier(() => {
                        if (lastNotify === notify) {
                            lastNotify = undefined;
                            notify();
                        }
                    }, { urgent: true });
                }
            }
        };
    };
}
